import type { PartnerAppConfig } from "@expert/app-config/types";
import type { Partner } from "@expert/shared-types";

export const isProd = import.meta.env.VITE_ENV_TYPE === "production" && import.meta.env.PROD;

function createFeaturesStub({
    callbackLater = false,
    outboundCall = false,
    surveyEnabled = false,
    medialess = false,
    verificationType = "phoneNumber",
    isCustomerDetailsV2Enabled = false,
} = {}) {
    return {
        callbackLater,
        outboundCall,
        surveyEnabled,
        medialess,
        verificationType,
        isCustomerDetailsV2Enabled,
    };
}

function createPartnerConfigStub({
    features = createFeaturesStub(),
    activationSupportNumbers = [],
    programs = [],
    requiresManualCopy = false,
} = {}) {
    return {
        features,
        activationSupportNumbers,
        programs,
        requiresManualCopy,
    };
}

const configStub = {
    asurion: createPartnerConfigStub(),
    cricket: createPartnerConfigStub(),
    verizon: createPartnerConfigStub({
        features: createFeaturesStub({
            callbackLater: true,
            outboundCall: true,
        }),
    }),
    att: createPartnerConfigStub(),
    tmobile: createPartnerConfigStub({
        features: createFeaturesStub({
            outboundCall: true,
        }),
    }),
    mcafee: createPartnerConfigStub({
        features: createFeaturesStub({
            medialess: true,
            verificationType: "email",
        }),
    }),
    aafes: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    adt: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    amazonrouter: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    asurionphonecare: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    asurionhomeplus: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    bluestream: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    brightspeed: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    cox: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    dish: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    ee: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    frontier: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    hotwire: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    koodo: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    leakprotect: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    liberty: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    mediacom: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    others: createPartnerConfigStub(),
    nexcom: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    samsungna: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    sparklight: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    staples: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    staplessmb: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    telus: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    telushomepro: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    thd: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    thesource: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    uscellular: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    viasat: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    fios: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    optimum: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    microsoft: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    life360: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    reach: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    tracfone: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    dell: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    pilot: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    nrg: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    smartpay: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    asuriontechcare: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    attconnectedhome: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    optimumbiz: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    verizonconnectedhome: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    vzwsmb: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    xboxcareplus: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    yahoo: createPartnerConfigStub({
        features: createFeaturesStub({
            isCustomerDetailsV2Enabled: true,
        }),
    }),
    unknown: createPartnerConfigStub(),
};

const setupPartnersConfigStub = () => (partner: Partner) => {
    return (configStub[partner as keyof typeof configStub] ?? configStub.unknown) as unknown as PartnerAppConfig;
};

export const appConfigStub = {
    getPartnersConfig: setupPartnersConfigStub(),
};
